import { useQueryClient, useInfiniteQuery, useMutation } from '@tanstack/react-query'
import { useEffect } from 'react'
import dayjs from 'dayjs'
import styled from 'styled-components'

import { QUERY_KEY } from '../../constants'

import { useUserInfo } from '../../hooks/useUserInfo'

import { getNotificationList, readAllNotification } from '../../api/notice'

import { CenterWrap, ColumnItem } from '../../components/common'
import { Text } from '../../components/text'
import { Spinner } from '../../components/spinner/Spinner'
import { EmptyPage } from '../../components/exception/EmptyPage'
import { InfinityScroll } from '../../components/pagingList/InfinityScroll'

import { Color } from '../../theme/color'

export function MainNotification() {
  const queryClient = useQueryClient()
  const { data: userInfo } = useUserInfo()

  const { data, isPending, isError, hasNextPage, fetchNextPage } = useInfiniteQuery({
    queryKey: [QUERY_KEY.notificationList],
    queryFn: async ({ pageParam = 0 }) => await getNotificationList({ offset: pageParam, size: 50 }),
    getNextPageParam: (lastPage) => (lastPage.last ? undefined : lastPage.offset + lastPage.size),
    initialPageParam: 0,
    enabled: !!userInfo,
    select: (data) => ({
      ...data,
      pages: data.pages.map((page) => page.list).flat(),
    }),
  })
  const { mutate } = useMutation({
    mutationFn: readAllNotification,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [QUERY_KEY.notificationList] })
    },
  })

  useEffect(() => {
    if (!userInfo) return
    mutate()
  }, [userInfo])

  if (isPending) {
    return (
      <CenterWrap>
        <Spinner />
      </CenterWrap>
    )
  }

  if (!userInfo || isError) {
    return <EmptyPage />
  }

  return (
    <InfinityScroll length={data.pages.length} isLast={!hasNextPage} next={fetchNextPage}>
      {data.pages.map(({ id, title, contents, createdDate }) => (
        <ContentContainer key={id}>
          <ColumnItem>
            <Text size={14} weight={600} color="text_secondary">
              {title}
            </Text>
            <Text size={16} weight={500}>
              {contents}
            </Text>
            <Text size={12} color="text_secondary">
              {dayjs(createdDate).format('YYYY.MM.DD')}
            </Text>
          </ColumnItem>
        </ContentContainer>
      ))}
    </InfinityScroll>
  )
}

const ContentContainer = styled.div`
  padding: 0 24px;
  > ${ColumnItem} {
    gap: 6px;
    padding: 16px 0;
    border-bottom: 1px solid ${Color.line};
    > *:last-child {
      margin-top: 2px;
    }
  }
`
